/* ------------------------------------------------------------------ */
/*  Orchestrator – parse XML → decode → evaluate → return results     */
/* ------------------------------------------------------------------ */

import type { FullTestResult, LineResult, ParseMessage } from './types';
import { parseDecoders, parseRules } from './parser';
import { decodeAllLines } from './decoder-engine';
import { evaluateRules } from './rule-engine';

/**
 * Run the full Wazuh pipeline:
 *  1. Parse decoder XML and rule XML
 *  2. Decode each log line through the decoders
 *  3. Evaluate all rules against decoded results
 *  4. Return structured results
 */
export function runPipeline(
  logLines: string[],
  decoderXml: string,
  ruleXml: string
): FullTestResult {
  const messages: ParseMessage[] = [];

  // Filter out empty lines
  const lines = logLines.filter((l) => l.trim().length > 0);

  if (lines.length === 0) {
    messages.push({ type: 'warning', message: 'No log lines provided.' });
    return { lines: [], decoderCount: 0, ruleCount: 0, messages };
  }

  // Parse
  const { decoders, messages: decoderMessages } = parseDecoders(decoderXml);
  const { rules, messages: ruleMessages } = parseRules(ruleXml);
  messages.push(...decoderMessages, ...ruleMessages);

  // Decode
  const decodedLines = decodeAllLines(lines, decoders, messages);

  // Evaluate rules
  const firedRulesMap = evaluateRules(decodedLines, rules, messages);

  // Build results
  const lineResults: LineResult[] = lines.map((raw, i) => ({
    lineIndex: i,
    raw,
    decoded: decodedLines[i],
    firedRules: firedRulesMap.get(i) ?? [],
  }));

  return {
    lines: lineResults,
    decoderCount: decoders.length,
    ruleCount: rules.length,
    messages,
  };
}
